import './styles';
import { Config } from 'config/envConfig';
import Main from './components/Main';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import Login from './components/login';
import Signup from './components/signup';
import ForgotPassword from './components/ForgotPassword';
import ConfirmationPage from './components/ConfirmationPage';
import ChangePassword from './components/ChangePassword';
import Temp from './test/temp';
import MainInfo from './components/mainInfo';
import { Support, ContactUs } from './components/support';
import { useEffect, useRef, useState } from 'react';
import { SettingsView } from './components/settings';
import axios from 'axios';
import VerifyEmailBlockerPage from './components/verifyEmailBlockerPage';
import { useAuth } from './context/AuthContext';
import VerifyEmail from './components/verifyEmail';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ReportLayoutsView } from './components/reportLayouts';
import { getUrl } from './utils';
import io from 'socket.io-client';
import LandingPage from './landingpage/src/landingPage.jsx';
import TermsAndConditions from './landingpage/src/FooterPages/TermsOfUse.jsx';
import BrandGuidelines from './landingpage/src/FooterPages/BrandGuidelines.jsx';
import PrivacyPolicy from './landingpage/src/FooterPages/PrivacyPolicy.jsx';
import RefundsAndCancellations from './landingpage/src/FooterPages/RefundsAndCancellations.jsx';
import { DashboardView } from './components/dashboard/dashboardView.jsx';
import { alert, AlertType, Modal } from '@quyva/shared-ui';
import { AgentView } from './components/agentsToolfrontend/index.js'; // Adjust the path as needed
import {
    AuthStatus,
    TaskProgressType,
    Features,
    OtherFeatures,
    isFeatureEnabled,
} from '@quyva/common';
import SubscriptionDashboard from './components/SubscriptionDashboard';
import { useFeatureFlags } from './context';
import PricingPageOnLogin from './components/PricingPageOnLogin';
import { faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import { SUBSCRIPTION_PLANS } from './utils/planUtils.js';
import ChatAppRepoList from './components/chatApp/RepoList';
import ChatAppChat from './components/chatApp/Chat';
import { useGlobalCredits } from './context';
import { useLocation } from 'react-router-dom';
import TasksPage from './components/tasks/TasksPage.jsx';
import AgentsUpdatesPage from './components/agentsUpdates/AgentsUpdatesPage.jsx';

const PERSISTED_ROUTES = [
    '/',
    '/terms-and-conditions',
    '/privacy-policy',
    '/brand-guidelines',
    '/refund-and-cancellations',
    '/contactUs',

    // Auth & account
    '/login',
    '/signup',
    '/forgot-password',
    '/forgot-password/reset',
    '/confirmation',
    '/verify-email',
    '/verify-email-blocker',

    // App routes
    '/dashboard',
    '/agents',
    '/current-plan',
    '/chats',
    '/extraction',
    '/templates-and-layouts',
    '/settings',
    '/help',
    '/tasks',

    // ChatApp
    '/chatapp/repos',

    // Test / misc
    '/test',
];

const RoutePersistence = () => {
    const location = useLocation();
    const lastPathRef = useRef(null);

    useEffect(() => {
        const { pathname } = location;

        // Handle dynamic routes like /chatapp/chat/:repoId
        const isDynamicChatRoute = pathname.startsWith('/chatapp/chat/');

        const isAllowed =
            PERSISTED_ROUTES.includes(pathname) || isDynamicChatRoute;

        if (!isAllowed) return;

        // Prevent duplicate writes during auth hydration
        if (lastPathRef.current === pathname) return;

        lastPathRef.current = pathname;

        localStorage.setItem('last-route', pathname);
    }, [location.pathname]);

    return null;
};

const SOCKET_SERVER_URL = getUrl(
    `http://${Config.SOCKET_MANAGER_HOST}:${Config.SOCKET_MANAGER_PORT}`
);

export const defaultTheme = 'default';
export const defaultFont = 'segoe';

const fetchTemplateList = async (user, setActiveTemplate, setTemplateList) => {
    // Fetching selected template
    try {
        if (!user) {
            throw new Error('User not found!');
        }
        const dynamicUrl = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/get_template_by_email?email=${user.email}`
        );
        const selectedTemplateResponse = await fetch(dynamicUrl);
        const selectedTemplate = await selectedTemplateResponse.json();
        //${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}
        // Fetching template list
        const tempUrl = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/gettemplate`
        );
        const response = await axios.get(tempUrl, {
            params: {
                id: user.email,
            },
        });

        const templatesData = response.data || [];

        // Handle case where no templates exist
        if (!templatesData.length) {
            setTemplateList([]);
            setActiveTemplate(null);
            return;
        }

        // Find and set default template
        // if selected template is available, then set it as active template
        // else set first available default template as active template
        if (selectedTemplate?.template_name) {
            const matchedTemplate = templatesData.find(
                (data) =>
                    data.filename &&
                    data.filename === selectedTemplate.template_name
            );
            if (matchedTemplate) {
                setActiveTemplate(matchedTemplate);
            } else {
                // Selected template not found in list, fall back to default
                const defaultTemplates = templatesData.filter(
                    (template) =>
                        template.filename &&
                        template.filename.toLowerCase().startsWith('default_')
                );
                setActiveTemplate(
                    defaultTemplates.length ? defaultTemplates[0] : templatesData[0] || null
                );
            }
        } else {
            // if no selected template, then set first available default temp
            const defaultTemplates = templatesData.filter(
                (template) =>
                    template.filename &&
                    template.filename.toLowerCase().startsWith('default_')
            );

            if (defaultTemplates.length) {
                setActiveTemplate(defaultTemplates[0]);
            } else if (templatesData.length) {
                // No default templates, use first available template
                setActiveTemplate(templatesData[0]);
            } else {
                setActiveTemplate(null);
            }
        }

        setTemplateList(templatesData);
    } catch (error) {
        console.error('Error fetching templates:', error.message);
        // Set empty state on error instead of showing alert
        setTemplateList([]);
        setActiveTemplate(null);
    }
};

const getOnGoingProcesses = async (user) => {
    try {
        const dynamicUrl = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/getOnGoingProcesses?email=${user.email}`
        );

        const encodedToken = encodeURIComponent(user.token);
        const response = await axios.get(dynamicUrl, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: encodedToken,
            },
        });

        if (response) {
            const onGoingProcesses = response.data.onGoingProcesses;
            return onGoingProcesses ? onGoingProcesses : [];
        }
        return [];
    } catch (error) {
        alert(AlertType.ERROR, error.message);
        return [];
    }
};

const createId = (message) => {
    const processId =
        message.processId || message.processID || message.process_id || null;

    if (processId !== null && processId !== undefined && String(processId) !== '') {
        return `process-${String(processId)}`;
    }

    const userMail = message.userMail ? message.userMail : message.user_mail;
    const repoId =
        message.repoId !== undefined && message.repoId !== null
            ? message.repoId
            : message.repo_id;
    const repoName = message.repoName ? message.repoName : message.repo_name;
    const process = message.process;
    const repoKey =
        repoId !== null && repoId !== undefined && String(repoId) !== ''
            ? repoId
            : repoName;
    const id = `${userMail}-${repoKey}-${process}`;
    return id;
};

const createFallbackId = (message) => {
    const userMail = message.userMail ? message.userMail : message.user_mail;
    const repoId =
        message.repoId !== undefined && message.repoId !== null
            ? message.repoId
            : message.repo_id;
    const repoName = message.repoName ? message.repoName : message.repo_name;
    const process = message.process;
    const repoKey =
        repoId !== null && repoId !== undefined && String(repoId) !== ''
            ? repoId
            : repoName;
    return `${userMail}-${repoKey}-${process}`;
};

const getFallbackIdAliases = (message) => {
    const userMail = message.userMail ? message.userMail : message.user_mail;
    const repoId =
        message.repoId !== undefined && message.repoId !== null
            ? message.repoId
            : message.repo_id;
    const repoName = message.repoName ? message.repoName : message.repo_name;
    const process = message.process;

    return Array.from(
        new Set(
            [repoId, repoName]
                .filter((value) => value !== undefined && value !== null && String(value) !== '')
                .map((repoKey) => `${userMail}-${repoKey}-${process}`)
        )
    );
};

const getProcessingIdentityCandidates = (message) => {
    const primaryId = createId(message);
    const fallbackIds = getFallbackIdAliases(message);

    return Array.from(new Set([primaryId, ...fallbackIds].filter(Boolean)));
};

const isSameProcessingTask = (left, right) => {
    const leftIds = getProcessingIdentityCandidates(left);
    const rightIds = getProcessingIdentityCandidates(right);

    return leftIds.some((id) => rightIds.includes(id));
};

const toDebugTaskSnapshot = (message) => ({
    id: createId(message),
    fallbackId: createFallbackId(message),
    fallbackAliases: getFallbackIdAliases(message),
    processId: message.processId || message.processID || message.process_id || null,
    repoId:
        message.repoId !== undefined && message.repoId !== null
            ? message.repoId
            : message.repo_id,
    repoName: message.repoName || message.repo_name || '',
    process: message.process || '',
    progress: message.progress ?? message.status ?? '',
    creationTime: message.creationTime || message.creation_time || '',
    timeStamp: message.timeStamp || message.timestamp || '',
    message: message.message || '',
});

// Maps a notification_history row (snake_case, from GET /getOnGoingProcesses) into the
// shape the Tasks/notification components expect (camelCase, as the socket sends).
// Only used on the rehydrate-after-reload path.
const formatMessage = (messageData) => {
    return {
        id: messageData.id,
        // Carry the row's real step log through.
        //
        // This used to hard-code `Processing ${process} on ${repo_name}` and throw the
        // row's own message away, so a task rebuilt after a page refresh lost every
        // accumulated progress line. TasksPage reads `message` (and splits it on the
        // "\n\n" delimiter to render the step list), so it had nothing to show. The
        // synthetic label is now only the fallback for a row with no message.
        message:
            messageData.message ||
            `Processing ${messageData.process} on ${messageData.repo_name}`,
        // Socket payloads carry percentageCompletion; DB rows would carry the
        // snake_case column. Accept either so the progress bar survives a reload
        // wherever the value came from -- TasksPage does the same on its own input.
        percentageCompletion:
            typeof messageData.percentage_completion === 'number'
                ? messageData.percentage_completion
                : messageData.percentageCompletion,
        // Both are rendered by singleNotificationView; dropping them here meant a
        // rehydrated task showed no output block.
        output_text: messageData.output_text,
        output_files: messageData.output_files,
        process: messageData.process,
        progress: messageData.progress,
        repoId:
            messageData.repoId !== undefined && messageData.repoId !== null
                ? messageData.repoId
                : messageData.repo_id,
        repoName: messageData.repoName || messageData.repo_name,
        processId:
            messageData.processId || messageData.processID || messageData.process_id,
        timeStamp: messageData.timestamp,
        creationTime: messageData.creation_time || messageData.creationTime,
        userMail: messageData.user_mail,
    };
};

const isValidNotification = async (
    notification,
    notificationMap,
    user,
    setProcessingRepos,
    finishedRunIds
) => {
    const id = createId(notification);
    const fallbackIds = getFallbackIdAliases(notification);

    const effectiveProgress = notification?.progress ?? notification?.status;
    const progressLower =
        typeof effectiveProgress === 'string' ? effectiveProgress.toLowerCase() : '';
    const isPausedLike = progressLower === 'paused' || progressLower === 'waiting';

    if (effectiveProgress === TaskProgressType.PROCESSING || isPausedLike) {
        // A tick that lost the race with its own terminal notification. Dropping it
        // keeps the finished task from being resurrected as a second, live row.
        if (finishedRunIds && [id, ...fallbackIds].some((key) => finishedRunIds.has(key))) {
            return false;
        }
        if (notificationMap.has(id) || fallbackIds.some((fallbackId) => notificationMap.has(fallbackId))) {
            fallbackIds.forEach((fallbackId) => {
                if (fallbackId !== id && notificationMap.has(fallbackId)) {
                    notificationMap.delete(fallbackId);
                }
            });
            notificationMap.set(id, notification);
            return true;
        } else {
            // If start message is missed some how updated data is checked and entry is made accordingly
            const onGoingProcesses = await getOnGoingProcesses(user);
            setProcessingRepos(onGoingProcesses);

            const found = onGoingProcesses.find(
                (process) => isSameProcessingTask(process, notification)
            );
            if (found) {
                notificationMap.set(id, notification);
                return true;
            }
        }
    } else if (effectiveProgress === TaskProgressType.STARTED) {
        // A new run of the same repo: forget the previous run's terminal state.
        if (finishedRunIds) {
            [id, ...fallbackIds].forEach((key) => finishedRunIds.delete(key));
        }
        fallbackIds.forEach((fallbackId) => {
            if (fallbackId !== id && notificationMap.has(fallbackId)) {
                notificationMap.delete(fallbackId);
            }
        });
        notificationMap.set(id, notification);
        return true;
    } else {
        if (notificationMap.has(id)) {
            notificationMap.delete(id);
        }
        fallbackIds.forEach((fallbackId) => {
            if (fallbackId !== id && notificationMap.has(fallbackId)) {
                notificationMap.delete(fallbackId);
            }
        });
        if (finishedRunIds) {
            [id, ...fallbackIds].forEach((key) => finishedRunIds.add(key));
        }
        // End message is valid and should be sent to update state.
        return true;
    }

    return false;
};

const App = () => {
    const { featureFlagsData } = useFeatureFlags();
    const [activeTemplate, setActiveTemplate] = useState(null);
    const [templateList, setTemplateList] = useState([]);
    const { authenticated } = useAuth();
    const [newNotification, setNewNotification] = useState(null);
    const [viewNotifications, setViewNotifications] = useState(false);
    const [messages, setMessages] = useState([]);
    const [socket, setSocket] = useState(null);
    const [processingRepos, setProcessingRepos] = useState([]);
    const [displayFloatingButton, setDisplayFloatingButton] = useState(true);
    const [notificationMap, setNotificationMap] = useState(new Map());
    // Runs that already reported a terminal state. The server pushes the
    // terminal notification and a straggler 'processing' tick can still land
    // after it, which the getOnGoingProcesses() recovery below would treat as
    // a live run and re-add -- showing the task twice until a refresh.
    const [finishedRunIds] = useState(() => new Set());
    const [isSideViewExpanded, setIsSideViewExpanded] = useState(true);
    const user = JSON.parse(localStorage.getItem('current'));
    const { user: authUser } = useAuth();
    const { refreshUserData, isAdmin, isOrgAdmin, fetchUserPreferences } =
        useGlobalCredits();
    const [fileToChat, setFileToChat] = useState('');
    const [repo_to_chat, set_repo_to_chat] = useState(-1);
    const [repoToExtract, setRepoToExtract] = useState(null);
    const [chat_selected, set_chat_selected] = useState(false);
    const [workOnRepo, setWorkOnRepo] = useState(-1);
    const [workOnRepoExtraction, setWorkOnRepoExtraction] = useState(-1);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedLayout, setSelectedLayout] = useState(null);
    const [addNewTemplateMode, setAddNewTemplateMode] = useState(false);
    const [isCategorizationTemplateMode, setIsCategorizationTemplateMode] =
        useState(false);
    const [isCategorizationTemplate, setIsCategorizationTemplate] =
        useState(false);
    const [templateData, setTemplateData] = useState(null);
    const [retrievedTemplateData, setRetrievedTemplateData] = useState(null);
    const [templateIsSuccessful, setTemplateIsSuccessful] = useState(false);
    const [isRepoSortedAlphabetically, setIsRepoSortedAlphabetically] =
        useState(false);
    const [googleClientDriveId, setGoogleClientDriveId] = useState(Config.GOOGLE_CLIENT_DRIVE_ID || '');

    const [openWithTemplateDesigner, setOpenWithTemplateDesigner] =
        useState(false);

    // Add new state for manage subscription modal
    const [manageSubscriptionModal, setManageSubscriptionModal] =
        useState(false);
    const [planName, setPlanName] = useState(SUBSCRIPTION_PLANS.FREE);
    const authToken = encodeURIComponent(user?.token);
    const lastKnownRoute = localStorage.getItem('last-route');

    // run when user/authenticated changes
    useEffect(() => {
        // only when authenticated - will run after login completes
        if (authenticated === AuthStatus.AUTHENTICATED) {
            // prefer the Auth context user if available, else fallback to localStorage
            const candidateUser =
                authUser ??
                (() => {
                    try {
                        return JSON.parse(localStorage.getItem('current'));
                    } catch {
                        return null;
                    }
                })();

            if (candidateUser) {
                // call provider to refresh admin/org/preferences
                refreshUserData(candidateUser);
            }
        } else {
            // on logout, also clear user-specific state in GlobalState provider
            refreshUserData(null);
        }
    }, [authenticated, authUser, refreshUserData]);

    useEffect(() => {
        // Create a WebSocket connection
        const newSocket = io(SOCKET_SERVER_URL);
        setSocket(newSocket);

        // Listen for incoming messages
        newSocket.on('register', () => {
            if (authenticated === AuthStatus.AUTHENTICATED) {
                // Register the client to socket manager
                newSocket.emit('registerClient', user.email);
                // On 'registration' complete
                newSocket.on('registrationComplete', (socketId) => {
                    console.log(
                        'Registration with socket manager complete! Socket Id: ',
                        socketId
                    );
                });

                // Listen for incoming messages
                newSocket.on('statusUpdate', async (notification) => {
                    const isValid = await isValidNotification(
                        notification,
                        notificationMap,
                        user,
                        setProcessingRepos,
                        finishedRunIds
                    );
                    if (isValid) {
                        setNewNotification(notification);
                        // A finished repo-creation/file-upload task may have
                        // corrected a retired model, and the sweep can move the
                        // account's own default with it. Refetch so the model
                        // shown in the navbar is not stale. Cheap and idempotent;
                        // only on terminal states, not every progress tick.
                        const p = String(notification?.progress || '').toLowerCase();
                        if (p === 'completed' || p === 'warning') {
                            fetchUserPreferences();
                        }
                        setMessages((prevMessages) => {
                            const map = new Map();
                            (prevMessages || []).forEach((n) => {
                                map.set(createId(n), n);
                            });
                            const notificationFallbackIds = getFallbackIdAliases(notification);
                            (prevMessages || []).forEach((n) => {
                                const existingFallbackIds = getFallbackIdAliases(n);
                                const shouldReplaceExisting = existingFallbackIds.some((existingFallbackId) =>
                                    notificationFallbackIds.includes(existingFallbackId)
                                );
                                if (shouldReplaceExisting) {
                                    map.delete(createId(n));
                                }
                            });
                            map.set(createId(notification), notification);
                            const nextMessages = Array.from(map.values());
                            return nextMessages;
                        });
                    }
                });
            }
        });

        // Clean up the socket connection when the component unmounts
        return () => newSocket.close();
    }, [authenticated]);

    const fetchGoogleClientDriveId = async () => {
        try {
            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/getCloudConfig?keys=googleClientDriveId`
            );
            const response = await axios.get(dynamicUrl);
            if (response.data && response.data.googleClientDriveId) {
                setGoogleClientDriveId(response.data.googleClientDriveId);
            }
        } catch (error) {
            console.error('Error fetching Google Client Drive ID:', error.message);
        }
    };

    useEffect(() => {
        fetchGoogleClientDriveId();
    }, []);

    const fetchUserDetails = async () => {
        // Fetch current plan
        try {
            const planResponse = await axios.get(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${
                        Config.FAST_API_PORT
                    }/subscription_routes/current-plan/${encodeURIComponent(
                        user.email
                    )}`
                ),
                {
                    headers: {
                        Authorization: authToken,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (planResponse.data && planResponse.data.name) {
                setPlanName(planResponse.data.name);
                console.log('plan name', planResponse.data.name);
            } else {
                setPlanName(SUBSCRIPTION_PLANS.FREE);
            }
        } catch (planError) {
            console.error('Error fetching subscription plan');
            setPlanName(SUBSCRIPTION_PLANS.FREE);
            alert(
                AlertType.ERROR,
                'Failed to fetch subscription plan. Defaulting to FREE plan.'
            );
        }
    };

    useEffect(() => {
        (async () => {
            if (authenticated === AuthStatus.AUTHENTICATED) {
                // Getting processing list
                const onGoingProcesses = await getOnGoingProcesses(user);
                setProcessingRepos(onGoingProcesses);
                fetchUserDetails();

                if (
                    onGoingProcesses &&
                    onGoingProcesses.length === 0 &&
                    messages.length
                ) {
                    setMessages([]);
                } else {
                    // Adding notification to notificationMap to make related notification valid
                    if (onGoingProcesses.length) {
                        onGoingProcesses.forEach((process) => {
                            const modifiedData = formatMessage(process);
                            const id = createId(modifiedData);
                            notificationMap.set(id, modifiedData);
                        });
                    }

                    const newMessages = [...onGoingProcesses];
                    const modifiedMessages = newMessages.map((messageData) => {
                        return formatMessage(messageData);
                    });
                    modifiedMessages.sort((a, b) =>
                        a['repoName'].localeCompare(b['repoName'])
                    );
                    setMessages(modifiedMessages);
                }
            }
        })();

        return () => {};
    }, [authenticated]);

    useEffect(() => {
        if (
            newNotification &&
            newNotification.progress !== TaskProgressType.PROCESSING
        ) {
            setViewNotifications(true);
        }
        return () => {};
    }, [messages]);

    useEffect(() => {
        const messagesMap = new Map();
        messages.forEach((notification) => {
            const key = createId(notification);
            messagesMap.set(key, notification);
        });

        const reposProgressData = [...messagesMap.values()];
        const nextProcessingRepos = []

        // Rebuild processing state from current live messages only.
        // This avoids stale processing entries surviving in memory after a task
        // has already transitioned to a terminal state.
        reposProgressData.forEach((progressData) => {
            const progressLower =
                typeof progressData?.progress === 'string'
                    ? progressData.progress.toLowerCase()
                    : '';
            const isPausedLike =
                progressLower === 'paused' || progressLower === 'waiting';

            if (progressData.progress === TaskProgressType.PROCESSING || isPausedLike) {
                const found = nextProcessingRepos.find(
                    (task) => isSameProcessingTask(task, progressData)
                );
                if (!found) {
                    nextProcessingRepos.push(progressData);
                }
            }
        });

        setProcessingRepos(nextProcessingRepos);

        return () => {};
    }, [messages]);

    return (
        <GoogleOAuthProvider
            clientId={googleClientDriveId}
        >
            <BrowserRouter>
                <RoutePersistence />
                <Routes>
                    {/* Landing Page route */}
                    {authenticated === AuthStatus.AUTHENTICATED &&
                    isFeatureEnabled(Features.CHATS, featureFlagsData) ? (
                        <Route
                            path='/'
                            element={<Navigate to={'/chats'} replace />}
                        />
                    ) : (!featureFlagsData || isFeatureEnabled([Features.OTHERS, OtherFeatures.LANDING_PAGE], featureFlagsData)) ? (
                        <Route path='/' element={<LandingPage />} />
                    ) : (
                        <Route
                            path='/'
                            element={<Navigate to={'/login'} replace />}
                        />
                    )}

                    <Route
                        path='/terms-and-conditions'
                        element={<TermsAndConditions />}
                    />
                    <Route path='/privacy-policy' element={<PrivacyPolicy />} />
                    <Route
                        path='/brand-guidelines'
                        element={<BrandGuidelines />}
                    />
                    <Route
                        path='/refund-and-cancellations'
                        element={<RefundsAndCancellations />}
                    />
                    <Route path='/contactUs' element={<ContactUs />} />
                    {authenticated === AuthStatus.AUTHENTICATED ? (
                        <>
                            {isFeatureEnabled(
                                Features.DASHBOARD,
                                featureFlagsData
                            ) && (
                                <Route
                                    path='/dashboard'
                                    element={
                                        <DashboardView
                                            newNotification={newNotification}
                                            setNewNotification={
                                                setNewNotification
                                            }
                                            planName={planName}
                                            viewNotifications={
                                                viewNotifications
                                            }
                                            setViewNotifications={
                                                setViewNotifications
                                            }
                                            messages={messages}
                                            socket={socket}
                                            isSideViewExpanded={
                                                isSideViewExpanded
                                            }
                                            setIsSideViewExpanded={
                                                setIsSideViewExpanded
                                            }
                                        />
                                    }
                                />
                            )}
                            {isFeatureEnabled(
                                Features.TOOLS_AND_AGENTS,
                                featureFlagsData
                            ) && (
                                <Route
                                    path='/agents'
                                    element={
                                        <AgentView
                                            newNotification={newNotification}
                                            setNewNotification={
                                                setNewNotification
                                            }
                                            planName={planName}
                                            viewNotifications={
                                                viewNotifications
                                            }
                                            setViewNotifications={
                                                setViewNotifications
                                            }
                                            messages={messages}
                                            socket={socket}
                                            isSideViewExpanded={
                                                isSideViewExpanded
                                            }
                                            setIsSideViewExpanded={
                                                setIsSideViewExpanded
                                            }
                                        />
                                    }
                                />
                            )}

                            {isFeatureEnabled(
                                Features.BILLING,
                                featureFlagsData
                            ) && (
                                <Route
                                    path='/current-plan'
                                    element={
                                        <SubscriptionDashboard
                                            setManageSubscriptionModal={
                                                setManageSubscriptionModal
                                            }
                                            manageSubscriptionModal={
                                                manageSubscriptionModal
                                            }
                                        />
                                    }
                                />
                            )}
                            {isFeatureEnabled(
                                Features.CHATS,
                                featureFlagsData
                            ) && (
                                <Route
                                    path='/chats'
                                    element={
                                        <Main
                                            isAdmin={isAdmin}
                                            planName={planName}
                                            fetchTemplateList={
                                                fetchTemplateList
                                            }
                                            setTemplateList={setTemplateList}
                                            setActiveTemplate={
                                                setActiveTemplate
                                            }
                                            newNotification={newNotification}
                                            setNewNotification={
                                                setNewNotification
                                            }
                                            viewNotifications={
                                                viewNotifications
                                            }
                                            setViewNotifications={
                                                setViewNotifications
                                            }
                                            templateList={templateList}
                                            messages={messages}
                                            socket={socket}
                                            processingRepos={processingRepos}
                                            isSideViewExpanded={
                                                isSideViewExpanded
                                            }
                                            setIsSideViewExpanded={
                                                setIsSideViewExpanded
                                            }
                                            fileToChat={fileToChat}
                                            setFileToChat={setFileToChat}
                                            repo_to_chat={repo_to_chat}
                                            set_repo_to_chat={set_repo_to_chat}
                                            workOnRepo={workOnRepo}
                                            setWorkOnRepo={setWorkOnRepo}
                                            isRepoSortedAlphabetically={
                                                isRepoSortedAlphabetically
                                            }
                                            setIsRepoSortedAlphabetically={
                                                setIsRepoSortedAlphabetically
                                            }
                                        />
                                    }
                                />
                            )}
                            {isFeatureEnabled(
                                Features.EXTRACTION,
                                featureFlagsData
                            ) && (
                                <Route
                                    path='/extraction'
                                    element={
                                        <MainInfo
                                            activeTemplate={activeTemplate}
                                            setActiveTemplate={
                                                setActiveTemplate
                                            }
                                            planName={planName}
                                            templateList={templateList}
                                            setTemplateList={setTemplateList}
                                            fetchTemplateList={
                                                fetchTemplateList
                                            }
                                            isAdmin={isAdmin}
                                            newNotification={newNotification}
                                            setNewNotification={
                                                setNewNotification
                                            }
                                            viewNotifications={
                                                viewNotifications
                                            }
                                            setViewNotifications={
                                                setViewNotifications
                                            }
                                            messages={messages}
                                            socket={socket}
                                            processingRepos={processingRepos}
                                            isSideViewExpanded={
                                                isSideViewExpanded
                                            }
                                            setIsSideViewExpanded={
                                                setIsSideViewExpanded
                                            }
                                            setOpenWithTemplateDesigner={
                                                setOpenWithTemplateDesigner
                                            }
                                            fileToChat={fileToChat}
                                            setFileToChat={setFileToChat}
                                            repoToExtract={repoToExtract}
                                            setRepoToExtract={setRepoToExtract}
                                            chat_selected={chat_selected}
                                            set_chat_selected={
                                                set_chat_selected
                                            }
                                            workOnRepo={workOnRepoExtraction}
                                            setWorkOnRepo={
                                                setWorkOnRepoExtraction
                                            }
                                            isRepoSortedAlphabetically={
                                                isRepoSortedAlphabetically
                                            }
                                            setIsRepoSortedAlphabetically={
                                                setIsRepoSortedAlphabetically
                                            }
                                        />
                                    }
                                />
                            )}
                            {isFeatureEnabled(
                                Features.TEMPLATE,
                                featureFlagsData
                            ) && (
                                <Route
                                    path='/templates-and-layouts'
                                    element={
                                        <ReportLayoutsView
                                            activeTemplate={activeTemplate}
                                            setActiveTemplate={
                                                setActiveTemplate
                                            }
                                            planName={planName}
                                            templateList={templateList}
                                            setTemplateList={setTemplateList}
                                            fetchTemplateList={
                                                fetchTemplateList
                                            }
                                            newNotification={newNotification}
                                            setNewNotification={
                                                setNewNotification
                                            }
                                            viewNotifications={
                                                viewNotifications
                                            }
                                            setViewNotifications={
                                                setViewNotifications
                                            }
                                            messages={messages}
                                            socket={socket}
                                            isSideViewExpanded={
                                                isSideViewExpanded
                                            }
                                            setIsSideViewExpanded={
                                                setIsSideViewExpanded
                                            }
                                            openWithTemplateDesigner={
                                                openWithTemplateDesigner
                                            }
                                            setOpenWithTemplateDesigner={
                                                setOpenWithTemplateDesigner
                                            }
                                            selectedTemplate={selectedTemplate}
                                            setSelectedTemplate={
                                                setSelectedTemplate
                                            }
                                            selectedLayout={selectedLayout}
                                            setSelectedLayout={
                                                setSelectedLayout
                                            }
                                            addNewTemplateMode={
                                                addNewTemplateMode
                                            }
                                            setAddNewTemplateMode={
                                                setAddNewTemplateMode
                                            }
                                            isCategorizationTemplateMode={
                                                isCategorizationTemplateMode
                                            }
                                            setIsCategorizationTemplateMode={
                                                setIsCategorizationTemplateMode
                                            }
                                            isCategorizationTemplate={
                                                isCategorizationTemplate
                                            }
                                            setIsCategorizationTemplate={
                                                setIsCategorizationTemplate
                                            }
                                            templateData={templateData}
                                            setTemplateData={setTemplateData}
                                            retrievedTemplateData={
                                                retrievedTemplateData
                                            }
                                            setRetrievedTemplateData={
                                                setRetrievedTemplateData
                                            }
                                            templateIsSuccessful={
                                                templateIsSuccessful
                                            }
                                            setTemplateIsSuccessful={
                                                setTemplateIsSuccessful
                                            }
                                        />
                                    }
                                />
                            )}
                            {isFeatureEnabled(
                                Features.SETTINGS,
                                featureFlagsData
                            ) && (
                                <>
                                    <Route
                                        path='/settings'
                                        element={
                                            <SettingsView
                                                newNotification={
                                                    newNotification
                                                }
                                                setNewNotification={
                                                    setNewNotification
                                                }
                                                planName={planName}
                                                viewNotifications={
                                                    viewNotifications
                                                }
                                                setViewNotifications={
                                                    setViewNotifications
                                                }
                                                messages={messages}
                                                socket={socket}
                                                isSideViewExpanded={
                                                    isSideViewExpanded
                                                }
                                                setIsSideViewExpanded={
                                                    setIsSideViewExpanded
                                                }
                                            />
                                        }
                                    />
                                </>
                            )}

                            <Route
                                path='/verify-email'
                                element={<VerifyEmail />}
                            />
                            <Route
                                path='/tasks'
                                element={
                                    <TasksPage
                                        newNotification={newNotification}
                                        setNewNotification={setNewNotification}
                                        viewNotifications={viewNotifications}
                                        setViewNotifications={setViewNotifications}
                                        messages={messages}
                                        socket={socket}
                                    />
                                }
                            />

                            <Route
                                path='/agents-updates'
                                element={
                                    isOrgAdmin ? (
                                        <AgentsUpdatesPage
                                            newNotification={newNotification}
                                            setNewNotification={setNewNotification}
                                            viewNotifications={viewNotifications}
                                            setViewNotifications={setViewNotifications}
                                            messages={messages}
                                            socket={socket}
                                        />
                                    ) : (
                                        <Navigate to={'/tasks'} replace />
                                    )
                                }
                            />
                            {isFeatureEnabled(
                                Features.SUPPORT,
                                featureFlagsData
                            ) && (
                                <Route
                                    path='/help'
                                    element={
                                        <Support
                                            newNotification={newNotification}
                                            setNewNotification={
                                                setNewNotification
                                            }
                                            planName={planName}
                                            viewNotifications={
                                                viewNotifications
                                            }
                                            setViewNotifications={
                                                setViewNotifications
                                            }
                                            messages={messages}
                                            socket={socket}
                                            isSideViewExpanded={
                                                isSideViewExpanded
                                            }
                                            setIsSideViewExpanded={
                                                setIsSideViewExpanded
                                            }
                                            displayFloatingButton={
                                                displayFloatingButton
                                            }
                                            setDisplayFloatingButton={
                                                setDisplayFloatingButton
                                            }
                                        />
                                    }
                                />
                            )}

                            {/* ChatApp Routes */}
                            <Route
                                path='/chatapp/repos'
                                element={<ChatAppRepoList />}
                            />
                            <Route
                                path='/chatapp/chat/:repoId'
                                element={<ChatAppChat />}
                            />

                            <Route path='/test' element={<Temp />} />
                            <Route
                                path='/forgot-password/reset'
                                element={<ChangePassword />}
                            />
                            <Route
                                path='/login'
                                element={<Navigate to={'/chats'} replace />}
                            />
                            <Route
                                path='/signup'
                                element={<Navigate to={'/chats'} replace />}
                            />
                            <Route
                                path='*'
                                element={
                                    <Navigate to={lastKnownRoute} replace />
                                }
                            />
                        </>
                    ) : (
                        <>
                            {authenticated ===
                            AuthStatus.REQUIRE_MAIL_VERIFICATION ? (
                                <>
                                    <Route
                                        path='/verify-email-blocker'
                                        element={<VerifyEmailBlockerPage />}
                                    />
                                    <Route
                                        path='/verify-email'
                                        element={<VerifyEmail />}
                                    />
                                    <Route
                                        path='/forgot-password/reset'
                                        element={<ChangePassword />}
                                    />
                                    <Route
                                        path='*'
                                        element={
                                            <Navigate
                                                to={'/verify-email-blocker'}
                                                replace
                                            />
                                        }
                                    />
                                </>
                            ) : (
                                <>
                                    <Route
                                        path='/verify-email'
                                        element={<VerifyEmail />}
                                    />
                                    <Route
                                        path='/forgot-password'
                                        element={<ForgotPassword />}
                                    />
                                    <Route
                                        path='/forgot-password/reset'
                                        element={<ChangePassword />}
                                    />
                                    <Route
                                        path='/confirmation'
                                        element={<ConfirmationPage />}
                                    />
                                    <Route path='/login' element={<Login />} />
                                    <Route
                                        path='/signup'
                                        element={<Signup />}
                                    />
                                    <Route
                                        path='*'
                                        element={
                                            <Navigate
                                                to={lastKnownRoute || '/login'}
                                                replace
                                            />
                                        }
                                    />
                                </>
                            )}
                        </>
                    )}
                </Routes>
                {manageSubscriptionModal && (
                    <Modal
                        title={'Manage Subscription'}
                        titleIcon={faExchangeAlt}
                        iconColor={'green'}
                        onClose={() => setManageSubscriptionModal(false)}
                    >
                        <PricingPageOnLogin />
                    </Modal>
                )}
            </BrowserRouter>
        </GoogleOAuthProvider>
    );
};

export default App;
